import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Car } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { calculateRepayment, formatCurrency } from '../../utils/financeCalc';
import SEOHead from '../../components/SEOHead';

export default function LeaseVsBuyCalculator() {
  const [assetPrice, setAssetPrice] = useState(95000);
  const [deposit, setDeposit] = useState(10000);
  const [interestRate, setInterestRate] = useState(8.25);
  const [termYears, setTermYears] = useState(5);
  const [balloonPercent, setBalloonPercent] = useState(20);

  const result = useMemo(
    () => calculateRepayment({ assetPrice: Number(assetPrice) || 0, deposit: Number(deposit) || 0, termYears: Number(termYears) || 1, interestRate: Number(interestRate) || 0, balloonPercent: Number(balloonPercent) || 0 }),
    [assetPrice, deposit, termYears, interestRate, balloonPercent]
  );

  return (
    <>
      <SEOHead
        title="Asset Finance Repayment Calculator | Flash Funding"
        description="Calculate repayments for vehicles and equipment with deposit, term, rate, and balloon structure."
        canonical="/lease-vs-buy-calculator"
      />

      <section className="py-16 sm:py-20" data-testid="asset-repayment-calculator-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="asset-repayment-input-card">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700" data-testid="asset-repayment-icon-pill"><Car size={14} /> Calculator</div>
            <h1 className="mb-3 text-3xl font-heading text-[#0F0A1A] sm:text-4xl" data-testid="asset-repayment-heading">Asset Finance Repayment Calculator</h1>
            <p className="mb-3 text-sm text-[#4A4556]" data-testid="asset-repayment-subheading">Model asset repayments clearly before speaking with lenders.</p>
            <p className="mb-6 text-xs text-[#6C657E]" data-testid="asset-repayment-route-priority-text">
              Start with /car-loans, /personal-loans, /commercial-car-loans, or /business-finance. Use /apply when ready.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-[#2E2742]">Asset Price (AUD)</Label>
                <Input type="number" value={assetPrice} onChange={(e) => setAssetPrice(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="asset-price-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Deposit (AUD)</Label>
                <Input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="asset-deposit-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Interest Rate (p.a.)</Label>
                <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="asset-rate-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Term (Years)</Label>
                <Input type="number" value={termYears} onChange={(e) => setTermYears(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="asset-term-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Balloon / Residual (%)</Label>
                <Input type="number" value={balloonPercent} onChange={(e) => setBalloonPercent(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="asset-balloon-input" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="asset-repayment-result-card">
            <h2 className="mb-6 text-xl font-semibold text-[#1B1431]">Estimated Structure</h2>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Loan Amount</span>
                <span className="font-mono text-[#1B1431]" data-testid="asset-loan-amount">{formatCurrency(result.loanAmount)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Estimated Monthly Repayment</span>
                <span className="font-mono text-purple-700" data-testid="asset-monthly-repayment">{formatCurrency(result.monthlyRepayment)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Balloon Amount</span>
                <span className="font-mono text-[#1B1431]" data-testid="asset-balloon-amount">{formatCurrency(result.balloonAmount)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Total Interest</span>
                <span className="font-mono text-[#1B1431]" data-testid="asset-total-interest">{formatCurrency(result.totalInterest)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5671]">Total Repayment</span>
                <span className="font-mono text-[#1B1431]" data-testid="asset-total-repayment">{formatCurrency(result.totalRepayment)}</span>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-purple-200 bg-[#f8f5fc] p-4">
              <p className="text-xs text-[#5C5671]" data-testid="asset-calc-disclaimer">Estimates only. Actual approval and pricing depends on lender policy and applicant profile.</p>
            </div>

            <div className="mt-8">
              <Link to="/business-finance" data-testid="asset-calculator-apply-link">
                <Button className="w-full rounded-full bg-purple-600 text-xs uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="asset-calculator-apply-button">
                  Check Your Options <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
